/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  UncontrolledDropdown,
} from 'reactstrap';
import { updateUserPermission } from '../../../actions/initiative';
import { BlockingLoader } from '@g17eco/atoms/loader';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { UserMin } from '@g17eco/types/user';
import { useAppSelector } from '../../../reducers';
import { FeaturePermissions } from '../../../services/permissions/FeaturePermissions';
import { useSiteAlert } from '../../../hooks/useSiteAlert';
import { getRootConfig } from '@selectors/globalData';
import { getChecked, getRoleName, handleRoleRadioChange, filterNonOwnerRoles } from '@utils/user';
import { RoleInformation, UserRoles } from '@constants/user';
import { UserTableUser } from '../types';

const getLabel = (roles: UserTableUser['roles']): string | JSX.Element => {
  if (roles.length === 0) {
    return 'None at this level';
  }
  if (roles.length === 1) {
    return getRoleName(roles[0]);
  }
  if (roles.includes(UserRoles.Owner)) {
    return getRoleName(UserRoles.Owner);
  }
  if (roles.length === 2 && [UserRoles.Contributor, UserRoles.Verifier].every(r => roles.includes(r))) {
    return getRoleName(UserRoles.ContributorVerifier);
  }
  return <>Multiple<i className='ml-2 fa fa-info-circle' /></>
}

const getDisabledMessage = (user: UserTableUser) => {
  if (user.isInherited) {
    return user.inheritedFrom
      ? `This role has been inherited from "${user.inheritedFrom}" and cannot be modified`
      : 'This role has been inherited and cannot be modified';
  }
  if (!user.isActivated) {
    return 'It is not possible to change roles for open invitations'
  }
}

export const UserRolesDropdown = ({ currentUser, user, initiativeId, roles, handleReload }:
  {
    currentUser: Pick<UserMin, '_id'>;
    user: UserTableUser;
    initiativeId: string;
    roles: RoleInformation[];
    handleReload: () => Promise<void>;
  }
) => {

  const [isSaving, setSaving] = useState(false);
  const { addSiteError } = useSiteAlert();
  const [isOpenPermission, setIsOpenPermission] = useState(false);

  const tooltip = user.roles.length === 1 ? undefined : <>
    {roles.filter(userRole => !userRole.disabled).map(userRole => (
      <div key={`${user._id}-${userRole.code}`}>
        <i className={`mr-2 fa ${getChecked(userRole.code, user.roles) ? 'fa-check' : 'fa-times'}`} />
        {getRoleName(userRole.code)}
      </div>
    ))}
  </>;

  const isCurrentUser = (user._id === currentUser._id);
  const disabledMessage = getDisabledMessage(user);
  const rootConfig = useAppSelector(getRootConfig);
  const canAccessVerification = FeaturePermissions.canAccessVerification(rootConfig);
  const isEnabled = (role: RoleInformation) => {
    if (role.requiresVerificationAccess && !canAccessVerification) {
      return false;
    }

    if (disabledMessage) {
      return false;
    }
    if (user.roles.includes(UserRoles.Owner) && role.code === UserRoles.Manager) {
      return !getChecked(role.code, user.roles); // allow owners to switch the admin role ON if they are not admin
    }
    if (isCurrentUser || user.roles.includes(UserRoles.Owner)) {
      // Since owners are implicit managers, viewers and users
      return [UserRoles.Contributor, UserRoles.Verifier, UserRoles.ContributorVerifier].includes(role.code);
    }
    return true;
  }

  const handleToggle = async (role: RoleInformation, action: 'add' | 'remove') => {
    if (!isEnabled(role)) {
      return;
    }
    setSaving(true);
    try {
      const newRoles = handleRoleRadioChange(role.code, action, user.roles);
      await updateUserPermission(initiativeId, user._id, filterNonOwnerRoles(newRoles));
      await handleReload();
      setSaving(false);
    }
    catch (e) {
      addSiteError(e);
      setSaving(false);
    }
  }

  const renderDropdownItem = (role: RoleInformation) => {
    const isManager = role.code === UserRoles.Manager;
    return (
      <DropdownItem key={`roles-${user._id}-${role.code}`} disabled={!isEnabled(role)}>
        <FormGroup
          className={isManager ? 'border-bottom pb-2 border-ThemeBorderDefault' : ''}
          disabled={!isEnabled(role)}
          onClick={(e) => e.stopPropagation()}
          check
        >
          <Input
            disabled={!isEnabled(role)}
            type={isManager ? 'checkbox' : 'radio'}
            id={`check-${user._id}-${role.code}`}
            checked={getChecked(role.code, user.roles)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleToggle(role, e.target.checked ? 'add' : 'remove')}
          />
          <Label check for={`check-${user._id}-${role.code}`}>
            {role.label}
          </Label>
        </FormGroup>
      </DropdownItem>
    );
  };

  return (
    <>
      {isSaving ? <BlockingLoader /> : null}
      <UncontrolledDropdown onToggle={(e, isOpen) => setIsOpenPermission(isOpen)}>
        <SimpleTooltip text={isOpenPermission ? '' : disabledMessage ?? tooltip}>
          <DropdownToggle color='transparent' disabled={Boolean(disabledMessage)} className={'text-left text-truncate'}>
            {getLabel(user.roles)}
          </DropdownToggle>
        </SimpleTooltip>
        <DropdownMenu>{roles.filter((role) => !role.disabled).map((role) => renderDropdownItem(role))}</DropdownMenu>
      </UncontrolledDropdown>
    </>
  );
}
